import { CircleAlert, Send } from 'lucide-react'
import { useState } from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'

import { AuthErrorBanner } from '@/customer/components/auth/auth-error-banner'
import { Alert, AlertDescription } from '@/customer/components/ui/alert'
import { Button } from '@/customer/components/ui/button'
import { PhoneInput } from '@/customer/components/ui/phone-input'

/**
 * Entry step of the "change my phone" flow. Owns the local draft +
 * E.164 validation; the caller receives the submitted number
 * through `onSubmit`.
 *
 * When `currentValue` is supplied, entering the same number is
 * blocked with a nudge to the verify page - matching better-auth's
 * phone plugin, which rejects `updatePhoneNumber` swaps to a number
 * already on the account.
 */
export function PhoneEntryPanel({
  currentValue,
  errorCode,
  isSubmitting,
  onSubmit,
}: {
  currentValue?: string | null
  errorCode: string | null
  isSubmitting: boolean
  onSubmit: (phone: string) => void | Promise<void>
}) {
  const [draft, setDraft] = useState('')
  const draftIsValid = Boolean(draft) && isValidPhoneNumber(draft)
  const isSameAsCurrent = draftIsValid && currentValue !== undefined && currentValue === draft

  return (
    <div className="flex flex-col gap-6">
      <p className="text-body-md text-foreground">
        Enter the new number you&apos;d like to use. We&apos;ll text you a 6-digit code to confirm
        it&apos;s yours.
      </p>

      <PhoneInput
        value={draft || undefined}
        onChange={(v: string | undefined) => setDraft(v ?? '')}
        error={errorCode !== null}
        autoComplete="tel"
        autoFocus
      />

      {isSameAsCurrent ? (
        <Alert variant="warning">
          <CircleAlert />
          <AlertDescription>That's your current number - try a different one.</AlertDescription>
        </Alert>
      ) : (
        <AuthErrorBanner code={errorCode} />
      )}

      <Button
        type="button"
        onClick={() => {
          if (draftIsValid && !isSameAsCurrent) void onSubmit(draft)
        }}
        disabled={!draftIsValid || isSameAsCurrent || isSubmitting}
        className="h-14 w-full"
      >
        <Send className="size-5" />
        Send code
      </Button>
    </div>
  )
}
